import * as Braze from "@braze/web-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import cardImages from "../../assets/images/cards";
import info from "../../assets/info.svg";
import lock from "../../assets/lock.svg";
import Button from "../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../components/button/Button.types";
import { CheckIcon } from "../../components/icons/CheckIcon";
import { ChevronDownIcon } from "../../components/icons/ChevronDownIcon";
import { ChevronUpIcon } from "../../components/icons/ChevronUpIcon";
import { CloseIcon } from "../../components/icons/CloseIcon";
import { Tag, TagState } from "../../components/tag";
import { FeatureFlags, useFeatureFlag } from "../../feature-flag";
import { useMetrics } from "../../metrics";
import useOnboarding from "../../onboarding/infrastructure/hooks/useOnboarding";
import { OnboardingTypes } from "../../onboarding/infrastructure/hooks/useOnboarding/useOnboarding.types";
import { OnboardingModal } from "../../onboarding/ui/components/onboarding-modal";
import { ROUTES } from "../../routes";
import { CountryISOCode } from "../home/restoreCompany";
import { useCompany } from "../home/useCompany";
import { useEmployee } from "../home/useEmployee";
import { Card, CardCategory, CardState, CardType, useCards } from "./useCards";
import { useRequestPinCard } from "./useRequestPinCard";

type Checked = {
  [key: string]: boolean;
};

const CardAction = ({
  title,
  description,
  image,
  children,
}: {
  title: string;
  description: string;
  image: string;
  children: JSX.Element;
}) => {
  return (
    <div className="flex flex-row items-center gap-3 justify-between py-3 px-4 mt-2">
      <div className="flex flex-row">
        <img
          src={image}
          className="w-11 h-11 rounded-xl bg-gray-50 p-3 mr-3"
          role="presentation"
          alt=""
        />
        <div className="flex flex-col justify-center items-start gap-1">
          <span className="text-sm text-gray-900 font-medium">{title}</span>
          <span className="text-xs font-normal text-gray-700">
            {description}
          </span>
        </div>
      </div>
      {children}
    </div>
  );
};

const CardImage = ({ src, altText }: { src: string; altText: string }) => (
  <img src={src} className="w-[74px] h-[46px] shrink-0 mr-2" alt={altText} />
);

const CardTitle = ({
  title,
  image,
  imageAltText,
  dropDownComponent,
  tagShow,
  tagState,
  tagText,
}: {
  title: string;
  expirationDate: string;
  image: string;
  imageAltText: string;
  dropDownComponent?: JSX.Element;
  tagShow: boolean;
  tagState: TagState;
  tagText: string;
}) => (
  <div className="flex align-center self-stretch p-4">
    <CardImage src={image} altText={imageAltText} />
    <div className="flex flex-col w-full justify-center">
      <span className="text-sm text-gray-900 font-medium mb-1">
        {title}
        {tagShow ? <Tag state={tagState} text={tagText} /> : null}
      </span>
    </div>
    {dropDownComponent}
  </div>
);

const Profile = () => {
  const { isEnabled: isEnabledPSD2 } = useFeatureFlag(FeatureFlags.EG_5040);
  const { trackAction, pageView } = useMetrics();
  const [checkedState, setCheckedState] = useState<Checked>({});
  const [requestPINCardModalVisible, setRequestPINCardModalVisible] =
    useState<boolean>(false);
  const [PINCardCode, setPINCardCode] = useState<string>();
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [
    accessibilityLabelForVirtualCardActive,
    setAccessibilityLabelForVirtualCardActive,
  ] = useState<boolean>(true);
  const { t } = useTranslation();
  const { cards, togglePhysicalCard, toggleVirtualCard } = useCards();
  const { requestPinCard, isLoading: isPinBeenFetching } = useRequestPinCard();
  const [isVisible, setIsVisible] = useState<{
    virtual: { [x: number]: boolean };
    physical: { [x: number]: boolean };
  }>({
    virtual: {},
    physical: {},
  });
  const { employee } = useEmployee();
  const navigate = useNavigate();
  const { data: onboarding } = useOnboarding();
  const { company } = useCompany();
  const { identify } = useMetrics();

  const physicalCards = useMemo(
    () =>
      cards?.filter(
        ({ isPhysical, categoryType, isCancelled, state }) =>
          isPhysical &&
          !isCancelled &&
          !(state === CardState.PENDING) &&
          (categoryType === CardCategory.PRIMARY ||
            categoryType === CardCategory.SECONDARY)
      ),
    [cards]
  );

  const virtualCards = useMemo(
    () =>
      cards?.filter(({ isVirtual, isCancelled }) => isVirtual && !isCancelled),
    [cards]
  );

  const isActive = (card: Card): boolean => {
    if (card && card.type === CardType.PHYSICAL) {
      return card?.activatedTimestamp
        ? card?.state === CardState.ACTIVE
        : false;
    }
    return card?.state === CardState.ACTIVE;
  };

  const handlerTogglePhysicalCard = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    togglePhysicalCard(event.target.name);
    trackAction.click({
      screenName: "Profile",
      componentName: "state toggle physical card",
      additionalProperties: {
        cardState: event.target.checked,
      },
    });

    setCheckedState({
      ...checkedState,
      [event.target.name]: event.target.checked,
    });
  };

  const handlerToggleVirtualCard = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const cardName = event.target.name;
    const cardState = event.target.checked;
    toggleVirtualCard(cardName, cardState);
    trackAction.click({
      screenName: "Profile",
      componentName: "state toggle virtual card",
      additionalProperties: {
        cardState,
      },
    });
    setAccessibilityLabelForVirtualCardActive(!cardState);

    setCheckedState({
      ...checkedState,
      [cardName]: cardState,
    });
  };

  const handlePhysicalPINCard = async (card: Card) => {
    try {
      const { source } = await requestPinCard({ cardId: card.cardId });
      trackAction.click({
        screenName: "Profile",
        componentName: "button request pin card",
        additionalProperties: {
          result: "success",
          cardId: card.cardId,
          cardType: card.type,
          categoryType: card.categoryType,
        },
      });
      if (source) {
        setPINCardCode(source);
        setRequestPINCardModalVisible(true);
        return;
      }
      setAlertVisible(true);
    } catch (e) {
      trackAction.click({
        screenName: "Profile",
        componentName: "button request pin card",
        additionalProperties: {
          result: "error",
          cardId: card.cardId,
          cardType: card.type,
          categoryType: card.categoryType,
        },
      });
      navigate(ROUTES.PAGE_FAILED, {
        state: {
          goBack: ROUTES.PROFILE,
          errorPage: "PINCardFailed",
        },
      });
    }
  };

  // const handleOnClickVirtualCardDetails = (cardId: string) => {
  //   trackAction.click({
  //     screenName: "Profile",
  //     componentName: "button virtualCardDetails",
  //   });
  //   navigate(`/profile/virtual-card-details/${cardId}`);
  // };

  const handleOnClickDropdown = (
    cardType: "virtual" | "physical",
    index: number
  ) => {
    setIsVisible((currentIsVisible) => ({
      ...currentIsVisible,
      [cardType]: {
        ...currentIsVisible[cardType],
        [index]: !currentIsVisible[cardType]?.[index],
      },
    }));
  };

  const handleClickOnCloseModal = () => {
    setRequestPINCardModalVisible(false);
  };

  const getPhysicalCardImage = (isActive: boolean, isMexico: boolean) => {
    if (isActive) {
      return isMexico ? cardImages.physicalCardMX : cardImages.physicalCard;
    }
    return isMexico
      ? cardImages.deactivatedPhysicalCardMX
      : cardImages.deactivatedPhysicalCard;
  };

  useEffect(() => {
    if (!employee || !company) return;
    identify(employee, company);
    Braze.changeUser(employee.id);
    Braze.automaticallyShowInAppMessages();
    Braze.openSession();
  }, [employee, company]);

  useEffect(() => {
    pageView("Profile");
  }, [pageView]);

  useEffect(() => {
    const tempCards: Checked = {};
    if (cards) {
      cards.map((card) => {
        tempCards[card.cardId] = isActive(card);
        if (card.type === CardType.VIRTUAL && card.state === CardState.ACTIVE) {
          setAccessibilityLabelForVirtualCardActive(false);
        }
      });
      setCheckedState(tempCards);
    }
  }, [cards]);

  const isOnboardingModalVisible =
    onboarding?.name === OnboardingTypes.ONBOARDING_STEPS;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6">
        <h1 className="text-gray-900 text-[28px] font-semibold leading-[42px]">
          {t("cards.greeting", { name: employee?.name })}
        </h1>
        <div className="flex flex-col">
          <h2 className="text-lg text-gray-900 font-medium">
            {t("cards.title")}
          </h2>
          <span className="text-sm font-normal text-gray-700">
            {t("cards.subtitle")}
          </span>
        </div>
        <div className="flex flex-col justify-center w-[600px] p-4 rounded-3xl bg-white">
          {physicalCards?.map((card, index) => (
            <div className="flex flex-col" key={index}>
              <CardTitle
                image={(() => {
                  const isActive = checkedState[card.cardId];
                  const isMexico = company?.country === CountryISOCode.MEXICO;

                  return getPhysicalCardImage(isActive, isMexico);
                })()}
                imageAltText={
                  checkedState[card.cardId]
                    ? t("cards.images.physical.active")
                    : t("cards.images.physical.deactivated")
                }
                title={t("cards.physical")}
                expirationDate={t("cards.expires", {
                  expirationDate: card.expirationIsoDate,
                })}
                dropDownComponent={
                  <button
                    className="flex min-w-[40px] min-h-[40px] justify-center items-center gap-[10px] rounded-full border-2 border-secondary-main self-center hover:bg-secondary-light"
                    aria-label={t("cards.dropdown.physical.button", {
                      number: index + 1,
                    })}
                    onClick={() => handleOnClickDropdown(card.type, index)}
                  >
                    {isVisible[card.type][index] ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </button>
                }
                tagShow={
                  card.state === CardState.ACTIVE ||
                  card.state === CardState.DEACTIVATED
                }
                tagState={
                  checkedState[card.cardId]
                    ? TagState.ACTIVE
                    : TagState.DEACTIVATED
                }
                tagText={
                  checkedState[card.cardId]
                    ? t("card.status.active")
                    : t("card.status.deactivated")
                }
              />

              <div
                aria-label={t("cards.dropdown.physical.content", {
                  number: index + 1,
                })}
                className={`flex flex-col gap-2  ${
                  !isVisible[card.type][index] && "hidden"
                }`}
              >
                {!isEnabledPSD2 ? (
                  <CardAction
                    title={t("cards.pin.title")}
                    description={t("cards.pin.description")}
                    image={cardImages.backOfACard}
                  >
                    <div className="w-[160px]">
                      <Button
                        onClick={() => handlePhysicalPINCard(card)}
                        disabled={isPinBeenFetching}
                        text={t("cards.pin.action")}
                        variant={ButtonVariant.OUTLINE}
                        size={ButtonSize.SMALL}
                      />
                    </div>
                  </CardAction>
                ) : (
                  <CardAction
                    image={info}
                    title={t("cards.details.title")}
                    description={t("cards.details.subtitle")}
                  >
                    <div className="w-[160px]"></div>
                  </CardAction>
                )}
                <CardAction
                  image={lock}
                  title={t("cards.activate.physical.title")}
                  description={t("cards.activate.physical.description")}
                >
                  <label
                    className="relative inline-flex items-center cursor-pointer"
                    htmlFor={card.cardId}
                  >
                    <input
                      type="checkbox"
                      checked={checkedState[card.cardId] ?? false}
                      name={card.cardId}
                      id={card.cardId}
                      className="sr-only peer"
                      onChange={handlerTogglePhysicalCard}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-main"></div>
                  </label>
                </CardAction>
              </div>
            </div>
          ))}
          {virtualCards?.length ? (
            <div
              className="h-[1px] bg-gray-100 m-4"
              data-testid="card-type-separator"
            />
          ) : null}
          {virtualCards?.map((card, index) => (
            <div className="flex flex-col" key={index}>
              <CardTitle
                image={
                  checkedState[card.cardId]
                    ? cardImages.virtualCard
                    : cardImages.deactivatedVirtualCard
                }
                imageAltText={
                  checkedState[card.cardId]
                    ? t("cards.images.virtual.active")
                    : t("cards.images.virtual.deactivated")
                }
                title={t("cards.virtual")}
                expirationDate={t("cards.expires", {
                  expirationDate: card.expirationIsoDate,
                })}
                tagShow={
                  card.state === CardState.ACTIVE ||
                  card.state === CardState.DEACTIVATED
                }
                tagState={
                  checkedState[card.cardId]
                    ? TagState.ACTIVE
                    : TagState.DEACTIVATED
                }
                tagText={
                  checkedState[card.cardId]
                    ? t("card.status.active")
                    : t("card.status.deactivated")
                }
                dropDownComponent={
                  <button
                    className="flex min-w-[40px] min-h-[40px] justify-center items-center rounded-full border-2 border-secondary-main self-center hover:bg-secondary-light"
                    aria-label={t("cards.dropdown.virtual.button")}
                    onClick={() => handleOnClickDropdown(card.type, index)}
                  >
                    {isVisible[card.type][index] ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </button>
                }
              />

              <div
                aria-label={t("cards.dropdown.virtual.content")}
                className={`flex flex-col gap-2 ${
                  !isVisible[card.type][index] && "hidden"
                }`}
              >
                <CardAction
                  image={info}
                  title={t("cards.details.title")}
                  description={t("cards.details.subtitle")}
                >
                  <div className="w-[160px]">
                    {/* <Button
                      disabled={isPinBeenFetching}
                      onClick={() =>
                        handleOnClickVirtualCardDetails(card.cardId)
                      }
                      text={t("cards.details.action")}
                      variant={ButtonVariant.OUTLINE}
                      size={ButtonSize.SMALL}
                    /> */}
                  </div>
                </CardAction>
                <CardAction
                  image={lock}
                  title={t("cards.activate.virtual.title")}
                  description={t("cards.activate.virtual.subtitle")}
                >
                  <label
                    className="relative inline-flex items-center cursor-pointer"
                    htmlFor={card.cardId}
                  >
                    <span className="hidden">
                      {accessibilityLabelForVirtualCardActive
                        ? t("cards.activate.virtual.accessibility.title")
                        : t("cards.deactivate.virtual.accessibility.title")}
                    </span>
                    <input
                      type="checkbox"
                      checked={checkedState[card.cardId] ?? false}
                      name={card.cardId}
                      id={card.cardId}
                      className="sr-only peer"
                      onChange={handlerToggleVirtualCard}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-main"></div>
                  </label>
                </CardAction>
              </div>
            </div>
          ))}
        </div>
      </div>
      {requestPINCardModalVisible && (
        <div
          className="relative z-10"
          role="dialog"
          aria-labelledby="modal-label"
          aria-modal="true"
        >
          <div className="fixed inset-0 z-10 w-screen flex min-h-full justify-center items-center bg-gray-700 bg-opacity-30">
            <div className="flex flex-col items-start gap-6 p-4 w-full max-w-lg bg-white px-4 pt-5 pb-4 rounded-xl shadow-lg relative">
              <h2
                id="modal-label"
                className="text-gray-900 text-xl font-semibold leading-7"
              >
                {t("cards.modal.pin.title")}
              </h2>
              <button
                type="button"
                className="absolute right-0 top-0 m-3"
                onClick={handleClickOnCloseModal}
                aria-label={t("cards.modal.pin.close")}
              >
                <CloseIcon />
              </button>
              <p className="text-gray-700 text-base font-normal leading-5">
                {t("cards.modal.pin.description")}
              </p>
              <img
                loading="eager"
                src={PINCardCode}
                alt="PIN"
                className="mx-auto w-full h-52"
              />
              <p className="text-gray-700 text-base font-normal leading-5">
                {t("cards.modal.pin.counter")}
              </p>
            </div>
          </div>
        </div>
      )}
      {alertVisible && (
        <div
          role="alert"
          aria-labelledby="alert-label"
          className="absolute flex flex-row p-4 w-80 items-start gap-2 bg-success-light rounded right-6 top-16 shadow-md opacity-35"
        >
          <span className="text-success-dark">
            <CheckIcon />
          </span>
          <div className="flex flex-col gap-1">
            <div className="flex justify-between">
              <h2
                id="alert-label"
                className="text-base not-italic font-medium leading-6 text-gray-900"
              >
                {t("cards.request.pin.success.title")}
              </h2>
              <button
                type="button"
                className="text-neutral-B700"
                onClick={() => setAlertVisible(false)}
                aria-label={t("cards.request.pin.success.close")}
              >
                <CloseIcon />
              </button>
            </div>
            <p className="text-sm not-italic font-normal leading-5 text-gray-900">
              {t("cards.request.pin.success.description")}
            </p>
          </div>
        </div>
      )}
      {isOnboardingModalVisible && <OnboardingModal />}
    </div>
  );
};

export { Profile };
